import { useState, useCallback, useEffect, useRef } from 'react';
import { getAgentList, getAgentDetails } from '../services/agents.js';
import { useChatApiClient } from '../context/ChatApiClientContext.jsx';

/** Page size for the agent list — paginated via infinite scroll in the picker. */
const AGENTS_PAGE_SIZE = 50;

/** Reads `hasNext` from a list `meta`, tolerating camelCase / snake_case. */
const metaHasNext = (meta, page, received, pageSize) => {
  if (meta && typeof meta === 'object') {
    if (meta.has_next != null || meta.hasNext != null) {
      return Boolean(meta.has_next ?? meta.hasNext);
    }
    const currentPage = Number(meta.current_page ?? meta.currentPage ?? page);
    const totalPages = Number(meta.total_pages ?? meta.totalPages ?? 0);
    if (totalPages > 0) return currentPage < totalPages;
  }
  // No usable meta: assume more only if the server filled the page.
  return received >= pageSize;
};

/**
 * Hook that handles the full agent logic for the main chat.
 * Calls getAgentList() service directly and keeps local state.
 * Architecture: shared services + local module state (no global stores).
 */
export const useHomeChatAgent = () => {
  const apiClient = useChatApiClient();
  const [agents, setAgents] = useState([]);
  const agentsRef = useRef([]);
  useEffect(() => { agentsRef.current = agents; }, [agents]);
  const [isLoadingAgents, setIsLoadingAgents] = useState(false);
  const [isLoadingMoreAgents, setIsLoadingMoreAgents] = useState(false);
  const [hasMoreAgents, setHasMoreAgents] = useState(false);
  const [error, setError] = useState(null);
  // The configured default ("New Chat") agent, fetched by id out-of-band so it
  // resolves even when it falls outside the current paginated page of agents.
  const [defaultAgent, setDefaultAgent] = useState(null);

  // Pagination cursor kept in refs so loadMoreAgents stays identity-stable and
  // always reads the latest page / search / guard without stale closures.
  const pageRef = useRef(1);
  const searchRef = useRef('');
  const hasMoreRef = useRef(false);
  const inFlightRef = useRef(false);
  // Monotonic request id: only the latest list request may apply its response,
  // so overlapping search / load-more / resume refetches can't clobber the
  // picker with a stale query or append another query's page.
  const requestSeqRef = useRef(0);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [conversationStarters, setConversationStarters] = useState([]);
  const [isLoadingAgentDetails, setIsLoadingAgentDetails] = useState(false);

  /**
   * Loads a page of the agent list from the service and maps it for the UI.
   *
   * @param {object} [params]
   * @param {number} [params.page=1] — 1-based page to request.
   * @param {number} [params.pageSize=AGENTS_PAGE_SIZE]
   * @param {string} [params.search] — name query; delegated to the backend.
   * @param {boolean} [params.append=false] — when true, append to the current
   *   list (infinite scroll) instead of replacing it (fresh load / new search).
   * @returns {Promise<object[]>} the mapped page just loaded.
   */
  const fetchAgents = useCallback(async (params = {}) => {
    const append = params.append === true;
    const page = params.page || 1;
    const pageSize = params.pageSize || AGENTS_PAGE_SIZE;
    const search =
      typeof params.search === 'string'
        ? params.search.trim()
        : (params.search?.query?.trim?.() ?? '');

    const seq = ++requestSeqRef.current;
    inFlightRef.current = true;
    try {
      if (append) setIsLoadingMoreAgents(true);
      else setIsLoadingAgents(true);
      setError(null);

      const response = await getAgentList(apiClient, {
        page,
        pageSize,
        ...(search ? { search } : {}),
      });
      // A newer request started while this one was in flight — drop this one so
      // it can't overwrite the newer query's results.
      if (seq !== requestSeqRef.current) return [];
      const agentsList = response.data?.agents || [];
      const meta = response.data?.meta || null;

      // Map API structure to expected format
      const mappedAgents = agentsList.map(agent => {
        const starters = (Array.isArray(agent.starters) && agent.starters.length > 0)
          ? agent.starters
          : (Array.isArray(agent.metadata?.conversation_starters) && agent.metadata.conversation_starters.length > 0)
            ? agent.metadata.conversation_starters
            : [];

        return {
          ...agent,
          id: agent.uuid, // Compatibility: id = uuid
          photo_url: agent.metadata?.imageUrl || agent.image || '',
          conversation_starters: starters
        };
      });

      pageRef.current = page;
      searchRef.current = search;
      const nextHasMore =
        metaHasNext(meta, page, agentsList.length, pageSize) && agentsList.length > 0;
      hasMoreRef.current = nextHasMore;
      setHasMoreAgents(nextHasMore);

      if (append) {
        setAgents(prev => {
          const seen = new Set(prev.map(a => a.uuid || a.id));
          const merged = prev.slice();
          for (const a of mappedAgents) {
            const key = a.uuid || a.id;
            if (key != null && seen.has(key)) continue;
            if (key != null) seen.add(key);
            merged.push(a);
          }
          return merged;
        });
      } else {
        setAgents(mappedAgents);
      }
      return mappedAgents;
    } catch (err) {
      if (seq !== requestSeqRef.current) return [];
      console.error('Error fetching agents:', err);
      setError(err);
      if (!append) {
        setAgents([]);
        hasMoreRef.current = false;
        setHasMoreAgents(false);
      }
      return [];
    } finally {
      // Only the latest request owns the loading flags / in-flight guard, so a
      // stale response finishing late doesn't clear the current request's state.
      if (seq === requestSeqRef.current) {
        if (append) setIsLoadingMoreAgents(false);
        else setIsLoadingAgents(false);
        inFlightRef.current = false;
      }
    }
  }, [apiClient]);

  /**
   * Loads the next page of agents and appends it (infinite scroll in the
   * picker). No-op while a request is in flight or when the last page is
   * reached. Carries the active search so paging keeps filtering server-side.
   */
  const loadMoreAgents = useCallback(() => {
    if (inFlightRef.current || !hasMoreRef.current) return undefined;
    return fetchAgents({
      page: pageRef.current + 1,
      search: searchRef.current,
      append: true,
    });
  }, [fetchAgents]);

  /**
   * Restores the base (unsearched) first page when the picker reopens after a
   * search. The agent list is shared state that search narrows in place; the
   * picker remounts on selection, so the search box clears but the narrowed
   * list would otherwise persist. No-op when no search is active.
   */
  const resetAgentsList = useCallback(() => {
    if (!searchRef.current) return undefined;
    return fetchAgents({});
  }, [fetchAgents]);

  /**
   * Fetches a single agent by id and stores it as the default agent. Used to
   * resolve the configured "New Chat" default independently of the paginated
   * list, so it works even when the default is not on the first page.
   *
   * @param {string} agentUuid
   * @returns {Promise<object|null>} the mapped default agent, or null.
   */
  const fetchDefaultAgent = useCallback(async (agentUuid) => {
    if (!agentUuid) return null;
    try {
      const response = await getAgentDetails(apiClient, agentUuid);
      const agent = response.data;
      if (!agent || !(agent.uuid || agent.id)) return null;
      const starters =
        (Array.isArray(agent.conversation_starters) && agent.conversation_starters.length > 0)
          ? agent.conversation_starters
          : (Array.isArray(agent.metadata?.conversation_starters) && agent.metadata.conversation_starters.length > 0)
            ? agent.metadata.conversation_starters
            : [];
      const mapped = {
        ...agent,
        id: agent.uuid || agent.id,
        photo_url: agent.metadata?.imageUrl || agent.image || '',
        conversation_starters: starters,
      };
      setDefaultAgent(mapped);
      return mapped;
    } catch (err) {
      console.error('Error fetching default agent:', err);
      return null;
    }
  }, [apiClient]);

  /**
   * Selects an agent by ID and updates related state.
   * When an agent is selected, fetches the latest agent details from the API
   * to get the conversation_starters from metadata.
   */
  const selectAgent = useCallback(async (agentId, agentData = null) => {
    setSelectedAgentId(agentId);
    if (agentData) {
      setSelectedAgent(agentData);
    }
    setIsLoadingAgentDetails(true);

    try {
      // Always fetch latest agent details from API to get conversation_starters
      const response = await getAgentDetails(apiClient, agentId);
      const fetchedAgent = response.data;

      if (fetchedAgent) {
        // Merge fetched data with passed agentData if provided (to preserve any local data)
        const mergedAgent = agentData 
          ? { ...agentData, ...fetchedAgent }
          : fetchedAgent;

        setSelectedAgent(mergedAgent);
        
        // Extract conversation_starters from metadata or direct property
        const starters = mergedAgent.conversation_starters || 
                        mergedAgent.metadata?.conversation_starters || 
                        [];
        
        setConversationStarters(Array.isArray(starters) ? starters : []);
      } else {
        // Fallback: if API call fails, use passed agentData or find in list
        if (agentData) {
          setSelectedAgent(agentData);
          const starters = agentData.conversation_starters || 
                          agentData.metadata?.conversation_starters || 
                          [];
          setConversationStarters(Array.isArray(starters) ? starters : []);
        } else {
          const agent = agentsRef.current.find(
            (a) => a.uuid === agentId || a.id === agentId
          );
          if (agent) {
            setSelectedAgent(agent);
            const starters = agent.conversation_starters || 
                            agent.metadata?.conversation_starters || 
                            [];
            setConversationStarters(Array.isArray(starters) ? starters : []);
          } else {
            setSelectedAgent(null);
            setConversationStarters([]);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching agent details:', error);
      
      // Fallback: use passed agentData or find in list
      if (agentData) {
        setSelectedAgent(agentData);
        const starters = agentData.conversation_starters || 
                        agentData.metadata?.conversation_starters || 
                        [];
        setConversationStarters(Array.isArray(starters) ? starters : []);
      } else {
        const agent = agentsRef.current.find(
          (a) => a.uuid === agentId || a.id === agentId
        );
        if (agent) {
          setSelectedAgent(agent);
          const starters = agent.conversation_starters || 
                          agent.metadata?.conversation_starters || 
                          [];
          setConversationStarters(Array.isArray(starters) ? starters : []);
        } else {
          setSelectedAgent(null);
          setConversationStarters([]);
        }
      }
    } finally {
      setIsLoadingAgentDetails(false);
    }
  }, [apiClient]);

  /**
   * Clears the agent selection.
   */
  const clearAgent = useCallback(() => {
    setSelectedAgent(null);
    setSelectedAgentId(null);
    setConversationStarters([]);
  }, []);

  /**
   * Finds an agent by ID in the loaded list.
   */
  const getAgentById = useCallback((agentId) => {
    return agents.find(
      (a) => a.uuid === agentId || a.id === agentId
    ) || null;
  }, [agents]);

  return {
    // Agent list
    agents,
    isLoadingAgents,
    isLoadingMoreAgents,
    hasMoreAgents,
    isLoadingAgentDetails,
    error,
    fetchAgents,
    loadMoreAgents,
    resetAgentsList,
    defaultAgent,
    fetchDefaultAgent,
    // Selected agent
    selectedAgent,
    setSelectedAgent,
    selectedAgentId,
    setSelectedAgentId,
    conversationStarters,
    setConversationStarters,
    // Actions
    selectAgent,
    clearAgent,
    getAgentById,
  };
};

export { useHomeChatAgent as useChatAgent };
